import React from 'react';
import { C } from '../../theme.js';
import { Button, FieldLabel, Select, TextInput } from '../../components/Primitives.jsx';
import { ColumnsContext } from '../StepInspector.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';
import { MultiCols } from '../shared/MultiCols.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { sourceColumns } from '../shared/sourceColumns.js';
import { outputNameIssue } from '../../data.js';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';

// When Source is something other than self, Group By collapses a chosen
// Source's own rows down to one row per key — a different row count/column
// set than the live pipeline payload, unlike Lookup/Arithmetic's per-row
// additions to self. So it can't silently become the new self downstream.
// Backend (executor.py) enforces this the same way it does for Unpivot: for
// a non-self Source, next_rows reverts to the unchanged self payload after
// this step, and the grouped result is only reachable through Output Name
// — required in that case, not optional.
export function GroupByInspector({ step, onChange, pipeline, secondaryDatasets, sheets, onBrowseJoinFile, runMap, columns }) {
  const c = step.config || {};
  const keys = c.keys || [];
  const aggs = c.aggs || [];
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  const sourceIsSelf = !c.source || c.source === '__self__';
  const liveCols = React.useContext(ColumnsContext);
  const sourceCols = sourceColumns(c.source, { sheets, secondaryDatasets, pipeline, runMap });
  const cols = (sourceIsSelf ? (columns || liveCols) : sourceCols) || [];

  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });
  const outputNameMissing = !sourceIsSelf && !(c.output_name || '').trim();

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <FieldLabel hint="Dataset to group/aggregate — an uploaded sheet, an earlier published step, or this block's own data">Source</FieldLabel>
        <SourceSelect
          value={c.source}
          onChange={(v) => u({ source: v })}
          secondaryDatasets={secondaryDatasets}
          sheets={sheets}
          pipeline={pipeline}
          currentStep={step}
          onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined}
          buildTargetName={pipeline?.inputFile?.buildTargetName}
        />
      </div>

      <FieldLabel>Group by</FieldLabel>
      <MultiCols columns={cols} selected={keys} onChange={(v) => u({ keys: v })} />
      <FieldLabel>Aggregations</FieldLabel>
      {(aggs.length ? aggs : [{ col: '', fn: 'count' }]).map((a, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <Select value={a.fn}
              onChange={(v) => u({ aggs: (aggs.length ? aggs : [{ col: '', fn: 'count' }]).map((x, j) => j === i ? { ...x, fn: v } : x) })}
              options={[
                { value: 'count', label: 'count' },
                { value: 'sum', label: 'sum' },
                { value: 'avg', label: 'avg' },
                { value: 'min', label: 'min' },
                { value: 'max', label: 'max' },
                { value: 'first', label: 'first' },
                { value: 'last', label: 'last' },
                { value: 'last_non_null', label: 'last non-null' },
                { value: 'first_last_average', label: 'first + last average' },
                { value: 'distinct', label: 'distinct count' },
              ]} style={{ flex: '0 0 120px' }} />
            <ColSelect value={a.col} columns={cols}
              onChange={(v) => u({ aggs: (aggs.length ? aggs : [{ col: '', fn: 'count' }]).map((x, j) => j === i ? { ...x, col: v } : x) })} />
          </div>
          <TextInput
            value={a.output_column || ''}
            onChange={(v) => u({ aggs: (aggs.length ? aggs : [{ col: '', fn: 'count' }]).map((x, j) => j === i ? { ...x, output_column: v } : x) })}
            placeholder={`Auto (e.g., ${a.fn}${a.col ? '_' + a.col : ''})`}
            style={{ fontSize: '.9rem' }}
          />
        </div>
      ))}
      <Button kind="ghost" size="sm" icon="＋"
        onClick={() => u({ aggs: [...aggs, { col: (cols[0]?.name || ''), fn: 'sum' }] })}>Add aggregation</Button>

      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={outputNameMissing}
        hint={sourceIsSelf
          ? 'Optional — also publish this result under a name so later blocks can pick it as their Source'
          : "Required — Source isn't self, so this step's grouped result doesn't touch the live pipeline payload; without a name it isn't reachable anywhere"}
        warningMessage={<>Source is &ldquo;{c.source}&rdquo;, not self — without an Output Name this step&rsquo;s result goes nowhere: the live pipeline payload stays unchanged and the grouped rows aren&rsquo;t saved anywhere.</>}
        placeholder="e.g., mas_long"
        issue={outputNameIssueRes}
      />
      <div style={{ color: C.fg4, fontSize: '.78rem', marginTop: 10, lineHeight: 1.5 }}>
        Every aggregation above runs against the same grouping key in this one step — add as many as you need (e.g. Last → M, avg → P for the same key) rather than chaining separate Group By steps.
      </div>
      <DecimalPlacesField value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
    </div>
  );
}
